import { useEffect, useState } from 'react';

import { getTrialDistanceLeft } from '~/utils/subscription';

const MS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;
const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * MINUTES_IN_HOUR;
const SECONDS_IN_DAY = SECONDS_IN_HOUR * HOURS_IN_DAY;

type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const getCountdownParts = (totalSeconds: number): CountdownParts => ({
  days: Math.floor(totalSeconds / SECONDS_IN_DAY),
  hours: Math.floor((totalSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR),
  minutes: Math.floor((totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE),
  seconds: totalSeconds % SECONDS_IN_MINUTE,
});

const getRemainingSeconds = (
  startDate: string,
  durationInDays: number,
): number => {
  return Math.floor(
    getTrialDistanceLeft(startDate, durationInDays) / MS_IN_SECOND,
  );
};

export const useCountdownTimer = (
  startDate: string,
  durationInDays: number,
): {
  countdown: CountdownParts;
  isFinished: boolean;
} => {
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    getRemainingSeconds(startDate, durationInDays),
  );

  useEffect(() => {
    if (remainingSeconds <= 0 || remainingSeconds >= SECONDS_IN_DAY) {
      return;
    }

    const interval = setInterval(() => {
      const next = getRemainingSeconds(startDate, durationInDays);

      if (next <= 0 || next >= SECONDS_IN_DAY) {
        clearInterval(interval);
        return;
      }

      setRemainingSeconds(next);
    }, MS_IN_SECOND);

    return (): void => clearInterval(interval);
  }, []);

  const countdownParts = getCountdownParts(remainingSeconds);

  return {
    countdown: countdownParts,
    isFinished: remainingSeconds <= 0,
  };
};
